import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "d-flex justify-center mb-6" }

import { ref, onMounted, watch } from 'vue';
import ImageCropper from '@/components/ImageCropper/ImageCropper.vue';
import RichTextEditor from '@/components/RichTextEditor/RichTextEditor.vue';
import { getSocialPlatforms, uploadImage } from '@/services/common/common.service';
import { getArticleTypes } from '@/services/news/article.service';
import type { EditableArticle, CreateArticleRequest, ArticleTypes } from '@/services/news/article.model';
import type { SocialPlatform } from '@/services/common/common.model';
import { compressImage } from '@/utils/image.util';

interface Props {
  initialData?: EditableArticle;
  onSubmit: (payload: CreateArticleRequest) => Promise<void>;
  loading?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ArticleForm',
  props: {
    initialData: {},
    onSubmit: { type: Function },
    loading: { type: Boolean }
  },
  setup(__props: any) {

const props = __props;

// Form fields
const title = ref('');
const slug = ref('');
const shortDescription = ref('');
const content = ref('');
const embeddedImages = ref<File[]>([]);
const image = ref<string | null>(null);
const imageFile = ref<File | null>(null);
const selectedType = ref<number | null>(null);
const links = ref<{ platformId: number | null; url: string; text: string }[]>([]);

const articleTypes = ref<ArticleTypes[]>([]);
const socialPlatforms = ref<SocialPlatform[]>([]);

const pageLoading = ref(true);
const errorMessage = ref('');

onMounted(async () => {
  try {
    const [typesRes, platformsRes] = await Promise.all([getArticleTypes(), getSocialPlatforms()]);
    articleTypes.value = typesRes ?? [];
    socialPlatforms.value = platformsRes ?? [];

    if (props.initialData) fillFormFromInitialData(props.initialData);
  } catch (err) {
    console.error(err);
    errorMessage.value = 'Failed to load required data.';
  } finally {
    pageLoading.value = false;
  }
});

watch(
  () => props.initialData,
  (val) => {
    if (val) fillFormFromInitialData(val);
  }
);

function fillFormFromInitialData(data: EditableArticle) {
  title.value = data.title;
  slug.value = data.slug;
  shortDescription.value = data.shortDescription;
  content.value = data.content;
  selectedType.value = data.typeId;
  image.value = data.image || null;

  links.value = (data.links || []).map((l) => ({
    platformId: l.id,
    url: l.url,
    text: l.text || '',
  }));
}

function onImageSelected(base64: string, file?: File) {
  image.value = base64;
  if (file) imageFile.value = file;
}

function addSocialLink() {
  links.value.push({ platformId: null, url: '', text: '' });
}

function removeSocialLink(index: number) {
  links.value.splice(index, 1);
}

async function handleSubmit() {
  errorMessage.value = '';

  if (!title.value || !slug.value || !shortDescription.value || !content.value || !selectedType.value) {
    errorMessage.value = 'Please fill in all required fields.';
    return;
  }

  let imageId: number | null = null;

  if (imageFile.value) {
    const compressed = await compressImage(imageFile.value, 1000, 0.8);
    const imgRes = await uploadImage(compressed);
    imageId = imgRes.imageId;
  } else if (props.initialData?.imageId) {
    imageId = props.initialData.imageId;
  } else {
    errorMessage.value = 'Please select and crop an image.';
    return;
  }

  let processedContent = content.value;

  const base64Matches = [...processedContent.matchAll(/<img[^>]+src=["'](data:[^"']+)["'][^>]*>/gi)];

  for (const match of base64Matches) {
    const fullTag = match[0];
    const base64Data = match[1];

    const response = await fetch(base64Data);
    const blob = await response.blob();
    const file = new File([blob], `embedded-${Date.now()}.png`, { type: blob.type });

    const compressed = await compressImage(file, 1000, 0.8);
    const uploadRes = await uploadImage(compressed);
    const newUrl = `/api/common/images/${uploadRes.imageId}`;

    const updatedTag = fullTag.replace(base64Data, newUrl);
    processedContent = processedContent.replace(fullTag, updatedTag);
  }

  const payload: CreateArticleRequest = {
    title: title.value,
    slug: slug.value,
    shortDescription: shortDescription.value,
    content: processedContent,
    imageId,
    typeId: selectedType.value,
    links: links.value
      .filter((l) => l.platformId && l.url)
      .map((l) => ({
        id: l.platformId!,
        url: l.url,
        text: l.text,
      })),
  };

  try {
    await props.onSubmit(payload);
  } catch (err: any) {
    console.error(err);
    errorMessage.value = err?.message || 'Failed to save article.';
  }
}

return (_ctx: any,_cache: any) => {
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_textarea = _resolveComponent("v-textarea")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_form = _resolveComponent("v-form")!

  return (pageLoading.value)
    ? (_openBlock(), _createBlock(_component_v_progress_circular, {
        key: 0,
        indeterminate: "",
        color: "primary",
        class: "d-flex mx-auto my-6"
      }))
    : (_openBlock(), _createBlock(_component_v_form, {
        key: 1,
        onSubmit: _withModifiers(handleSubmit, ["prevent"])
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_text_field, {
            modelValue: title.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((title).value = $event)),
            label: "Title",
            outlined: "",
            required: ""
          }, null, 8, ["modelValue"]),
          _createVNode(_component_v_text_field, {
            modelValue: slug.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((slug).value = $event)),
            label: "Slug",
            outlined: "",
            required: ""
          }, null, 8, ["modelValue"]),
          _createVNode(_component_v_textarea, {
            modelValue: shortDescription.value,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((shortDescription).value = $event)),
            label: "Short Description",
            outlined: "",
            required: ""
          }, null, 8, ["modelValue"]),
          _createVNode(_component_v_select, {
            modelValue: selectedType.value,
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((selectedType).value = $event)),
            items: articleTypes.value,
            "item-title": "name",
            "item-value": "id",
            label: "Article Type",
            outlined: "",
            required: ""
          }, null, 8, ["modelValue", "items"]),
          _createVNode(ImageCropper, {
            modelValue: image.value,
            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((image).value = $event)),
            class: "my-4",
            label: "Article Image",
            onChange: onImageSelected,
            aspectRatio: 5 / 3
          }, null, 8, ["modelValue"]),
          _createVNode(RichTextEditor, {
            modelValue: content.value,
            "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((content).value = $event)),
            label: "Content",
            embeddedImages: embeddedImages.value,
            "onUpdate:embeddedImages": _cache[6] || (_cache[6] = ($event: any) => ((embeddedImages).value = $event))
          }, null, 8, ["modelValue", "embeddedImages"]),
          _cache[11] || (_cache[11] = _createElementVNode("div", { class: "text-h6 mt-6 mb-3" }, "Social Links", -1)),
          _createElementVNode("div", _hoisted_1, [
            _createVNode(_component_v_btn, {
              color: "secondary",
              variant: "elevated",
              onClick: addSocialLink
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_icon, { start: "" }, {
                  default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                    _createTextVNode("mdi-plus", -1)
                  ]))]),
                  _: 1
                }),
                _cache[8] || (_cache[8] = _createTextVNode(" Add Social Link ", -1))
              ]),
              _: 1
            })
          ]),
          (links.value.length)
            ? (_openBlock(), _createBlock(_component_v_row, {
                key: 0,
                dense: ""
              }, {
                default: _withCtx(() => [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(links.value, (link, index) => {
                    return (_openBlock(), _createBlock(_component_v_col, {
                      key: index,
                      cols: "12",
                      class: "py-2"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_card, {
                          class: "pa-4 d-flex align-center flex-wrap gap-4",
                          elevation: "1",
                          rounded: "lg"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_select, {
                              modelValue: link.platformId,
                              "onUpdate:modelValue": ($event: any) => ((link.platformId) = $event),
                              items: socialPlatforms.value,
                              "item-title": "name",
                              "item-value": "id",
                              label: "Platform",
                              outlined: "",
                              "hide-details": "",
                              class: "social-input"
                            }, null, 8, ["modelValue", "onUpdate:modelValue", "items"]),
                            _createVNode(_component_v_text_field, {
                              modelValue: link.url,
                              "onUpdate:modelValue": ($event: any) => ((link.url) = $event),
                              label: "URL",
                              outlined: "",
                              "hide-details": "",
                              class: "social-input"
                            }, null, 8, ["modelValue", "onUpdate:modelValue"]),
                            _createVNode(_component_v_text_field, {
                              modelValue: link.text,
                              "onUpdate:modelValue": ($event: any) => ((link.text) = $event),
                              label: "Description",
                              outlined: "",
                              "hide-details": "",
                              class: "social-input"
                            }, null, 8, ["modelValue", "onUpdate:modelValue"]),
                            _createVNode(_component_v_btn, {
                              icon: "",
                              color: "error",
                              variant: "tonal",
                              class: "delete-btn",
                              onClick: ($event: any) => (removeSocialLink(index))
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_v_icon, null, {
                                  default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                                    _createTextVNode("mdi-delete", -1)
                                  ]))]),
                                  _: 1
                                })
                              ]),
                              _: 1
                            }, 8, ["onClick"])
                          ]),
                          _: 2
                        }, 1024)
                      ]),
                      _: 2
                    }, 1024))
                  }), 128))
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          (errorMessage.value)
            ? (_openBlock(), _createBlock(_component_v_alert, {
                key: 1,
                type: "error",
                variant: "tonal",
                class: "mt-2"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(errorMessage.value), 1)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          _createVNode(_component_v_btn, {
            loading: props.loading,
            type: "submit",
            color: "primary",
            block: "",
            class: "mt-4"
          }, {
            default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
              _createTextVNode(" Save Article ", -1)
            ]))]),
            _: 1
          }, 8, ["loading"])
        ]),
        _: 1
      }))
}
}

})